// Which products the three homepage shelves show — the same logic for every theme, so the shelves differ.
//
//   fresh    "New arrivals / Latest"        newest products
//   featured "Featured / Special / Picks"   products the vendor flagged "Feature on storefront"
//   sale     "On sale / Best sellers / Deals" discounted products, biggest discount first
//
// The shelves are filled one after another, each from products no earlier shelf has used:
// New arrivals first, then Featured, then On sale. A shelf that runs short is backfilled from products
// already used elsewhere, but only as many as it needs, so a few repeats are possible and never all of them.
// A shelf with fewer than 2 products is hidden (unless nothing else could be shown).

const MIN_SHELF = 2;
const byNewest = (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""));
const inStockFirst = (a, b) => (Number(b.stock > 0) - Number(a.stock > 0));

/** The product's discount in %, using its cheapest variant when it has variants. */
export function discountPct(p) {
  let price = p.price;
  let compare = p.compare_at_price;
  if (p.variant_items?.length) {
    const v = p.variant_items.reduce((m, x) => (x.price < m.price ? x : m));
    price = v.price;
    compare = v.compare_at_price;
  }
  return compare && price > 0 && compare > price ? Math.round((1 - price / compare) * 100) : 0;
}

export function homeSets(products = [], { fresh = 4, featured = 4, sale = 4 } = {}) {
  const all = products.filter(Boolean);
  const sorted = (list, ...cmps) => [...list].sort((a, b) => { for (const c of cmps) { const r = c(a, b); if (r) return r; } return 0; });

  const newest = sorted(all, inStockFirst, byNewest);
  const flagged = sorted(all.filter((p) => p.featured), inStockFirst, byNewest);
  const discounted = sorted(all.filter((p) => discountPct(p) > 0), inStockFirst, (a, b) => discountPct(b) - discountPct(a), byNewest);

  const used = new Set();
  const take = (primary, n, backfill = []) => {
    const out = [];
    for (const p of primary) { if (out.length >= n) break; if (!used.has(p.id)) { out.push(p); used.add(p.id); } }
    for (const p of backfill) { if (out.length >= n) break; if (!out.some((x) => x.id === p.id)) out.push(p); }   // reuse only to make up the shortfall
    return out;
  };

  const a = take(newest, fresh);
  // Featured: the vendor's flagged products; if there are too few, other unused products — non-discounted ones first, so
  // the discounted products stay available for the On-sale shelf — and only then reused flagged ones.
  const spare = newest.filter((p) => !used.has(p.id));
  const b = take(flagged, featured, [...spare.filter((p) => !discountPct(p)), ...spare.filter((p) => discountPct(p)), ...flagged]);
  b.forEach((p) => used.add(p.id));
  const c = take(discounted, sale, discounted);                      // only ever discounted products

  const lists = { fresh: a, featured: b, sale: c };
  const shown = Object.fromEntries(Object.entries(lists).map(([k, v]) => [k, v.length >= MIN_SHELF ? v : []]));
  if (!Object.values(shown).some((v) => v.length)) shown.fresh = newest.slice(0, fresh);   // a very small catalogue still gets one shelf
  return shown;
}
